export const MapModeEnum = { INDOOR: "INDOOR", NORMAL: "NORMAL" };

// ─────────────────────────────────────────────────────────────────────────────
// Action Types
// ─────────────────────────────────────────────────────────────────────────────
export const ActionTypes = {
  // SearchPin
  SEARCH_PIN_CLEAR: "SEARCH_PIN_CLEAR",
  SEARCH_PIN_DROP: "SEARCH_PIN_DROP",
  SEARCH_PIN_SET_VISIBLE: "SEARCH_PIN_SET_VISIBLE",

  // GPS
  GPS_SET_COORDS: "GPS_SET_COORDS",
  GPS_SET_MARKER: "GPS_SET_MARKER",
  GPS_SET_HEADING: "GPS_SET_HEADING",
  GPS_SET_NEAREST_CAMPUS: "GPS_SET_NEAREST_CAMPUS",
  GPS_SET_SNACKBAR: "GPS_SET_SNACKBAR",
  GPS_DISMISS_SNACKBAR: "GPS_DISMISS_SNACKBAR",
  GPS_RESET_MARKER: "GPS_RESET_MARKER",

  // Map
  MAP_SET_READY: "MAP_SET_READY",
  MAP_CHANGE_CAMPUS: "MAP_CHANGE_CAMPUS",
  MAP_INITIALIZE: "MAP_INITIALIZE",
  MAP_SET_MODE: "MAP_SET_MODE",
  MAP_SET_FLOORS: "MAP_SET_FLOORS",
  MAP_SET_FLOOR_INDEX: "MAP_SET_FLOOR_INDEX",
  MAP_SET_INDOOR: "MAP_SET_INDOOR",
  MAP_RESET_INDOOR: "MAP_RESET_INDOOR",

  // UI
  UI_OPEN_DIALOG: "UI_OPEN_DIALOG",
  UI_CLOSE_DIALOG: "UI_CLOSE_DIALOG",
  UI_SET_ICON_GUIDE_SNACKBAR: "UI_SET_ICON_GUIDE_SNACKBAR",

  // Campus Change (複合action)
  CAMPUS_CHANGE_FULL: "CAMPUS_CHANGE_FULL",
};

// ─────────────────────────────────────────────────────────────────────────────
// SearchPin Reducer
// ─────────────────────────────────────────────────────────────────────────────
export const searchPinInitialState = {
  visible: false,
  x: null,
  y: null,
  floorId: null,
  buildingId: null,
};

export function searchPinReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SEARCH_PIN_CLEAR:
      return searchPinInitialState;
    case ActionTypes.SEARCH_PIN_DROP:
      return {
        visible: true,
        x: action.x,
        y: action.y,
        floorId: action.floorId,
        buildingId: action.buildingId,
      };
    case ActionTypes.SEARCH_PIN_SET_VISIBLE:
      return { ...state, visible: action.visible };
    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// GPS Reducer
// ─────────────────────────────────────────────────────────────────────────────
export const gpsInitialState = {
  coords: { lat: null, lng: null, accuracy: null },
  markerVisible: false,
  markerX: null,
  markerY: null,
  accuracyRadius: 0,
  headingDeg: null,
  nearestCampus: null,
  showNearestCampusSnackbar: false,
  nearestCampusSnackbarDismissed: false,
};

export function gpsReducer(state, action) {
  switch (action.type) {
    case ActionTypes.GPS_SET_COORDS:
      return { ...state, coords: action.coords };
    case ActionTypes.GPS_SET_MARKER:
      return {
        ...state,
        markerVisible: action.visible,
        markerX: action.x,
        markerY: action.y,
        accuracyRadius:
          action.accuracyRadius !== undefined
            ? action.accuracyRadius
            : state.accuracyRadius,
      };
    case ActionTypes.GPS_SET_HEADING:
      return { ...state, headingDeg: action.deg };
    case ActionTypes.GPS_SET_NEAREST_CAMPUS:
      return { ...state, nearestCampus: action.campusId };
    case ActionTypes.GPS_SET_SNACKBAR:
      return { ...state, showNearestCampusSnackbar: action.show };
    case ActionTypes.GPS_DISMISS_SNACKBAR:
      return {
        ...state,
        showNearestCampusSnackbar: false,
        nearestCampusSnackbarDismissed: true,
      };
    case ActionTypes.GPS_RESET_MARKER:
      return {
        ...state,
        markerVisible: false,
        markerX: null,
        markerY: null,
        accuracyRadius: 0,
        headingDeg: null,
      };
    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Map Reducer
// ─────────────────────────────────────────────────────────────────────────────
export const createMapInitialState = (initialCampus) => ({
  isReady: false,
  currentCampus: initialCampus,
  currentCampusCode: null,
  currentMode: MapModeEnum.NORMAL,
  currentMapJson: {},
  currentBuildingArray: [],
  currentSelectedFloors: [],
  currentSelectedFloorIndex: 0,
  currentFloorArray: [],
  currentRoomArray: [],
  currentSearchArray: [],
});

export function mapReducer(state, action) {
  switch (action.type) {
    case ActionTypes.MAP_SET_READY:
      return { ...state, isReady: action.isReady };
    case ActionTypes.MAP_CHANGE_CAMPUS:
      return {
        ...state,
        currentCampus: action.campus,
        isReady: false,
      };
    case ActionTypes.MAP_INITIALIZE:
      return {
        ...state,
        currentMapJson: action.currentMapJson,
        currentBuildingArray: action.currentBuildingArray,
        currentFloorArray: action.currentFloorArray,
        currentRoomArray: action.currentRoomArray,
        currentSearchArray: action.currentSearchArray,
        currentCampusCode: action.currentCampusCode,
        currentMode: MapModeEnum.NORMAL,
        currentSelectedFloors: [],
        currentSelectedFloorIndex: 0,
      };
    case ActionTypes.MAP_SET_MODE:
      return { ...state, currentMode: action.mode };
    case ActionTypes.MAP_SET_FLOORS:
      return { ...state, currentSelectedFloors: action.floors };
    case ActionTypes.MAP_SET_FLOOR_INDEX:
      return { ...state, currentSelectedFloorIndex: action.index };
    case ActionTypes.MAP_SET_INDOOR:
      return {
        ...state,
        currentMode: MapModeEnum.INDOOR,
        currentSelectedFloors: action.floors,
        currentSelectedFloorIndex: action.index,
      };
    case ActionTypes.MAP_RESET_INDOOR:
      return {
        ...state,
        currentMode: MapModeEnum.NORMAL,
        currentSelectedFloors: [],
        currentSelectedFloorIndex: 0,
      };
    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// UI Reducer
// ─────────────────────────────────────────────────────────────────────────────
export const uiInitialState = {
  dialogIsOpen: false,
  selectedMarker: {
    id: "",
    name: "",
    images: [],
    description: "",
    properties: [],
  },
  showIconGuideSnackbar: false,
};

export function uiReducer(state, action) {
  switch (action.type) {
    case ActionTypes.UI_OPEN_DIALOG:
      return { ...state, dialogIsOpen: true, selectedMarker: action.marker };
    case ActionTypes.UI_CLOSE_DIALOG:
      return { ...state, dialogIsOpen: false };
    case ActionTypes.UI_SET_ICON_GUIDE_SNACKBAR:
      return { ...state, showIconGuideSnackbar: action.show };
    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Action Creators
// ─────────────────────────────────────────────────────────────────────────────

// SearchPin Actions
export const searchPinActions = {
  clear: () => ({ domain: "searchPin", type: ActionTypes.SEARCH_PIN_CLEAR }),
  drop: (x, y, floorId, buildingId) => ({
    domain: "searchPin",
    type: ActionTypes.SEARCH_PIN_DROP,
    x,
    y,
    floorId,
    buildingId,
  }),
  setVisible: (visible) => ({
    domain: "searchPin",
    type: ActionTypes.SEARCH_PIN_SET_VISIBLE,
    visible,
  }),
};

// GPS Actions
export const gpsActions = {
  setCoords: (coords) => ({
    domain: "gps",
    type: ActionTypes.GPS_SET_COORDS,
    coords,
  }),
  setMarker: (visible, x, y, accuracyRadius) => ({
    domain: "gps",
    type: ActionTypes.GPS_SET_MARKER,
    visible,
    x,
    y,
    accuracyRadius,
  }),
  setHeading: (deg) => ({
    domain: "gps",
    type: ActionTypes.GPS_SET_HEADING,
    deg,
  }),
  setNearestCampus: (campusId) => ({
    domain: "gps",
    type: ActionTypes.GPS_SET_NEAREST_CAMPUS,
    campusId,
  }),
  setSnackbar: (show) => ({
    domain: "gps",
    type: ActionTypes.GPS_SET_SNACKBAR,
    show,
  }),
  dismissSnackbar: () => ({
    domain: "gps",
    type: ActionTypes.GPS_DISMISS_SNACKBAR,
  }),
  resetMarker: () => ({ domain: "gps", type: ActionTypes.GPS_RESET_MARKER }),
};

// Map Actions
export const mapActions = {
  setReady: (isReady) => ({
    domain: "map",
    type: ActionTypes.MAP_SET_READY,
    isReady,
  }),
  changeCampus: (campus) => ({
    domain: "map",
    type: ActionTypes.MAP_CHANGE_CAMPUS,
    campus,
  }),
  initialize: (payload) => ({
    domain: "map",
    type: ActionTypes.MAP_INITIALIZE,
    ...payload,
  }),
  setMode: (mode) => ({ domain: "map", type: ActionTypes.MAP_SET_MODE, mode }),
  setFloors: (floors) => ({
    domain: "map",
    type: ActionTypes.MAP_SET_FLOORS,
    floors,
  }),
  setFloorIndex: (index) => ({
    domain: "map",
    type: ActionTypes.MAP_SET_FLOOR_INDEX,
    index,
  }),
  setIndoor: (floors, index) => ({
    domain: "map",
    type: ActionTypes.MAP_SET_INDOOR,
    floors,
    index,
  }),
  resetIndoor: () => ({ domain: "map", type: ActionTypes.MAP_RESET_INDOOR }),
};

// UI Actions
export const uiActions = {
  openDialog: (marker) => ({
    domain: "ui",
    type: ActionTypes.UI_OPEN_DIALOG,
    marker,
  }),
  closeDialog: () => ({ domain: "ui", type: ActionTypes.UI_CLOSE_DIALOG }),
  setIconGuideSnackbar: (show) => ({
    domain: "ui",
    type: ActionTypes.UI_SET_ICON_GUIDE_SNACKBAR,
    show,
  }),
};
